import { Box, Stack } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import * as poiState from '../../../../state';
import { useGeneralFields, useTriggerPoiEditing } from '../../hooks';
import { GeneralFieldsForm } from '../GeneralFieldsForm';
import {
  PoiAccessibilityEnum,
  useUpdatePoiGeneralFieldsMutation,
  useGetAllTeamsForCreateOrEditPoiQuery,
  AllPointOfInterestsForDataLoaderDocument,
  PointOfInterestDocument,
  PointOfInterestsCountByStatusDocument,
  CuboidInput,
} from '@/__generated__/graphql';
import { StyledForm } from './styles';
import { SaveCancelFormButtons } from '../SaveCancelFormButtons';
import { convertTimeStampToDate, upperSnakeCase } from '@/utils';
import { enumToString } from '@/utils/string';
import { saveCancelButtonCommonStyles } from '../../../styles';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import * as cuboidsState from '../../../../../cuboids/state';
import { useAnalysisCuboidActions } from '../../../../../cuboids/hooks';
import { AnalysisCuboid } from '@/types';
import { LinkedPoiEditor } from '../LinkedPoiEditor';
import { PoiFormSubHeading } from '../styles';
import { allVoiTemplates as allVoiTemplatesState } from '@/components/Analysis/state';
import { useIsPoiWorkScope } from '@/hooks/useIsPoiWorkScope';

export const UpdateGeneralFields = () => {
  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);
  const setPoiFilteredByCheckbox = useSetAtom(poiState.poiFilteredByCheckbox);
  const currentCuboid = useAtomValue(cuboidsState.currentCuboid);
  const isCuboidEditorEnabled = useAtomValue(cuboidsState.isCuboidEditorEnabled);
  const allVoiTemplates = useAtomValue(allVoiTemplatesState);

  const poi = editPointOfInterest?.pointOfInterest;
  const [loading, setLoading] = useState(false);
  const navbarHeight = useNavbarHeight();
  const { checkByType } = useIsPoiWorkScope();

  const formattedDueDate = poi?.dueDate ? convertTimeStampToDate(poi?.dueDate) : undefined;

  const modifyCuboidDataInPoi = useCallback(
    (updatedData: AnalysisCuboid, targetId: string) => {
      setPoiFilteredByCheckbox((current) => {
        const copy = [...current];
        const updatedPoiIndex = copy.findIndex(({ id }) => id === targetId);
        if (updatedPoiIndex === -1) return current;

        const updatedPoi = {
          ...copy[updatedPoiIndex],
          cuboids: [updatedData],
        };

        copy[updatedPoiIndex] = updatedPoi;

        return copy;
      });
    },
    [setPoiFilteredByCheckbox]
  );

  const generalFieldsData = useGeneralFields({
    assignee: poi?.assignee || undefined,
    dueDate: formattedDueDate,
    name: poi?.name || undefined,
    poiId: poi?.poiId || undefined,
    status: poi?.status || undefined,
    visibility: poi?.visibility || undefined,
    teamIds: poi?.teams?.map(({ id }) => id) || [],
    workpackNumber: poi?.workpackNumber || undefined,
    comment: poi?.comment || undefined,
    accessibility: poi?.accessibility || undefined,
  });
  const { data: teamsData, loading: teamsLoading } = useGetAllTeamsForCreateOrEditPoiQuery();

  const teamOptions = useMemo(() => {
    return teamsData?.allTeams?.map(({ id, name }) => ({
      label: name,
      value: id,
    }));
  }, [teamsData?.allTeams]);

  const [mutate] = useUpdatePoiGeneralFieldsMutation({});

  const { showSnackBar } = useSnackBarMessage({});

  const { cancelEditing } = useTriggerPoiEditing();

  const { stopEditingPoiCuboids, doneEditingPoiCuboids } = useAnalysisCuboidActions();

  const isVoi = allVoiTemplates.some(
    (voiTemplate) => voiTemplate.toLowerCase() === enumToString(poi?.type ?? '').toLowerCase()
  );

  const isWorkScope = checkByType(poi?.type || '');

  const shouldIncludeCuboids = useMemo(() => {
    if (!currentCuboid) return false;
    return isWorkScope || isVoi;
  }, [isVoi, isWorkScope, currentCuboid]);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLElement>) => {
      event.preventDefault();
      setLoading(true);
      const stringifiedDueDate = generalFieldsData.state.dueDate
        ? String(generalFieldsData.state.dueDate)
        : undefined;

      const accessibilityEnum = Object.values(PoiAccessibilityEnum).find(
        (accessibilityValue) =>
          accessibilityValue === upperSnakeCase(generalFieldsData.state.accessibility || '')
      );
      // Do not pass poiId for update mutation since this id is not user defined
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { poiId, ...generalFieldsState } = generalFieldsData.state;
      mutate({
        variables: {
          input: {
            pointOfInterestId: poi?.id || '',
            ...generalFieldsState,
            dueDate: stringifiedDueDate || undefined,
            ...(accessibilityEnum ? { accessibility: accessibilityEnum } : {}),
            cuboids: shouldIncludeCuboids
              ? [
                  {
                    position: currentCuboid?.position,
                    scale: currentCuboid?.scale,
                    rotation: currentCuboid?.rotation,
                    // currentCuboid is already check for undefined
                    // in `shouldIncludeCuboids`
                  } as CuboidInput,
                ]
              : undefined,
            lastModifiedFor: 'Update General Fields',
          },
        },
        refetchQueries: [
          PointOfInterestDocument,
          AllPointOfInterestsForDataLoaderDocument,
          PointOfInterestsCountByStatusDocument,
        ],
      })
        .then((response) => {
          const newCuboids = response?.data?.updatePointOfInterest?.cuboids;
          if (newCuboids) {
            modifyCuboidDataInPoi(newCuboids[0] as AnalysisCuboid, poi?.id || '');
          }
          showSnackBar(`${isWorkScope ? 'Work scope' : 'Point of interest'} updated`, 'success');
          cancelEditing();
          doneEditingPoiCuboids();
        })
        .catch(() => {
          showSnackBar(
            `Could not update ${isWorkScope ? 'work scope' : 'point of interest'}`,
            'error'
          );
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [
      generalFieldsData.state,
      mutate,
      poi?.id,
      currentCuboid,
      showSnackBar,
      isWorkScope,
      cancelEditing,
      doneEditingPoiCuboids,
      modifyCuboidDataInPoi,
      shouldIncludeCuboids,
    ]
  );

  return (
    <Box height="100%">
      <StyledForm onSubmit={handleSubmit}>
        <Stack
          sx={{
            height: `calc(100vh / var(--zoom) - ${navbarHeight}px)`,
            overflowY: 'auto',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Box p={2} flex={1} mb="85px">
            <PoiFormSubHeading variant="h5">Edit General</PoiFormSubHeading>
            {isVoi && <LinkedPoiEditor />}
            <GeneralFieldsForm
              teamsLoading={teamsLoading}
              teamOptions={teamOptions}
              dataHook={generalFieldsData}
            />
          </Box>
          <SaveCancelFormButtons
            cancelButtonLabel="Cancel"
            buttonLabel="Save"
            loadingLabel="Updating..."
            onCancel={() => {
              stopEditingPoiCuboids(editPointOfInterest?.pointOfInterest);
              cancelEditing();
            }}
            loading={loading}
            sx={saveCancelButtonCommonStyles.sx}
            saveButtonStyles={saveCancelButtonCommonStyles.saveButtonStyles}
            cancelButtonStyles={saveCancelButtonCommonStyles.cancelButtonStyles}
            saveButtonDisabled={isCuboidEditorEnabled}
          />
        </Stack>
      </StyledForm>
    </Box>
  );
};
